/* ============================================
   MILKY WAY BACKGROUND COMPONENT
   The Creative Orchestra - GEMINI Framework
   Animated starry background with nebula effects
   ============================================ */

import { useMemo, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { generateStarPositions } from '@utils/helpers';

/**
 * Milky Way Background Component
 * Creates an immersive cosmic background with:
 * - Multiple layers of twinkling stars
 * - Nebula/cloud effects
 * - Subtle parallax movement
 * - Performance-optimized animations
 */
const MilkyWayBackground = ({ 
  starCount = 200,
  enableParallax = true,
  className = '',
}) => {
  const containerRef = useRef(null);
  const mousePosition = useRef({ x: 0.5, y: 0.5 });

  // Generate star positions once
  const stars = useMemo(() => {
    return generateStars(starCount);
  }, [starCount]);

  // Parallax effect on mouse move
  useEffect(() => {
    if (!enableParallax) return;

    const handleMouseMove = (e) => {
      mousePosition.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };

      if (containerRef.current) {
        const layers = containerRef.current.querySelectorAll('.parallax-layer');
        layers.forEach((layer, index) => {
          const depth = (index + 1) * 0.5;
          const moveX = (mousePosition.current.x - 0.5) * depth * 20;
          const moveY = (mousePosition.current.y - 0.5) * depth * 20;
          layer.style.transform = `translate(${moveX}px, ${moveY}px)`;
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [enableParallax]);

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-0 overflow-hidden bg-cosmos ${className}`}
      style={{
        background: 'linear-gradient(180deg, #0a0a0f 0%, #0f1419 30%, #0a0a12 70%, #050508 100%)',
      }}
    >
      {/* Base gradient layer */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(30, 30, 60, 0.3) 0%, transparent 70%)',
        }}
      />

      {/* Nebula layers */}
      <NebulaLayer 
        className="parallax-layer"
        color="rgba(99, 102, 241, 0.05)"
        position={{ x: '30%', y: '20%' }}
        size={600}
      />
      <NebulaLayer 
        className="parallax-layer"
        color="rgba(168, 85, 247, 0.04)"
        position={{ x: '70%', y: '60%' }}
        size={500}
      />
      <NebulaLayer 
        className="parallax-layer"
        color="rgba(59, 130, 246, 0.03)"
        position={{ x: '20%', y: '70%' }}
        size={400}
      />

      {/* Star layers */}
      <StarLayer 
        stars={stars.layer1} 
        className="parallax-layer"
        twinkleIntensity="high"
      />
      <StarLayer 
        stars={stars.layer2} 
        className="parallax-layer"
        twinkleIntensity="medium"
      />
      <StarLayer 
        stars={stars.layer3} 
        className="parallax-layer"
        twinkleIntensity="low"
      />

      {/* Shooting stars */}
      <ShootingStars />

      {/* Vignette overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 0%, rgba(0,0,0,0.4) 100%)',
        }}
      />

      {/* Milky Way band */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background: `
            linear-gradient(
              135deg,
              transparent 0%,
              transparent 35%,
              rgba(200, 200, 255, 0.02) 40%,
              rgba(200, 200, 255, 0.05) 50%,
              rgba(200, 200, 255, 0.02) 60%,
              transparent 65%,
              transparent 100%
            )
          `,
        }}
      />
    </div>
  );
};

/**
 * Generate stars for multiple layers
 */
function generateStars(count) {
  const layer1Count = Math.floor(count * 0.5);
  const layer2Count = Math.floor(count * 0.35);
  const layer3Count = count - layer1Count - layer2Count;

  return {
    layer1: Array.from({ length: layer1Count }, (_, i) => ({
      id: `l1-${i}`,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 1.5 + 0.5,
      opacity: Math.random() * 0.5 + 0.3,
      delay: Math.random() * 5,
      duration: Math.random() * 3 + 2,
    })),
    layer2: Array.from({ length: layer2Count }, (_, i) => ({
      id: `l2-${i}`,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 1,
      opacity: Math.random() * 0.4 + 0.2,
      delay: Math.random() * 5,
      duration: Math.random() * 4 + 3,
    })),
    layer3: Array.from({ length: layer3Count }, (_, i) => ({
      id: `l3-${i}`,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2.5 + 1.5,
      opacity: Math.random() * 0.3 + 0.1,
      delay: Math.random() * 5,
      duration: Math.random() * 5 + 4,
    })),
  };
}

/**
 * Star Layer Component
 * Renders a layer of twinkling stars
 */
const StarLayer = ({ 
  stars, 
  className = '',
  twinkleIntensity = 'medium',
}) => {
  const intensityConfig = {
    high: { min: 0.3, max: 1 },
    medium: { min: 0.4, max: 0.8 },
    low: { min: 0.5, max: 0.7 },
  };

  const { min, max } = intensityConfig[twinkleIntensity];

  return (
    <div className={`absolute inset-0 transition-transform duration-300 ${className}`}>
      {stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: star.size,
            height: star.size,
          }}
          animate={{
            opacity: [star.opacity * min, star.opacity * max, star.opacity * min],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: star.duration,
            delay: star.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

/**
 * Nebula Layer Component
 * Soft, glowing nebula effect
 */
const NebulaLayer = ({ 
  color, 
  position, 
  size,
  className = '',
}) => {
  return (
    <motion.div
      className={`absolute rounded-full blur-3xl transition-transform duration-500 ${className}`}
      style={{
        left: position.x,
        top: position.y,
        width: size,
        height: size,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        transform: 'translate(-50%, -50%)',
      }}
      animate={{
        scale: [1, 1.1, 1],
        opacity: [0.5, 0.7, 0.5],
      }}
      transition={{
        duration: 10,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />
  );
};

/**
 * Shooting Stars Component
 * Occasional shooting star animations
 */
const ShootingStars = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          className="absolute w-px bg-gradient-to-r from-transparent via-white to-transparent"
          style={{
            height: 100 + Math.random() * 100,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 50}%`,
            rotate: 45,
          }}
          initial={{ opacity: 0, x: -100, y: -100 }}
          animate={{
            opacity: [0, 1, 0],
            x: [0, 200],
            y: [0, 200],
          }}
          transition={{
            duration: 1,
            delay: i * 8 + Math.random() * 10,
            repeat: Infinity,
            repeatDelay: 15 + Math.random() * 20,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
};

export default MilkyWayBackground;
